import React from "react";
import { AbsoluteFill, OffthreadVideo, Sequence, interpolate, useCurrentFrame } from "remotion";
import { PAudio, PVideo, useLite } from "./media.js";
import { BehindTexts } from "./BehindText.js";
import { clipSeed, vary, windowStrength, timelineToSource, voiceFadeGain, type Clip, cutAt, styleInto, anyTransition } from "@hudhud/core";
import { FPS, HEIGHT, WIDTH, frameToMs, msToFrame } from "../types.js";
import { incomingMotion, leakPosition, outgoingMotion, type CutStyle, sparkles, transitionFrames } from "../math/transitions.js";
import { SRC_W, SRC_H, FIT_W, GRAIN_URI, GRAIN_TILE, grainOpacity, BARS_FRACTION, vignetteAlpha, lookFilter, gradeFilter, cropStyle, pieceFrame, pieceSpans, voiceGain, voiceJoinGain, voiceXfadeFrames, VOICE_XFADE_FRAMES } from "../math/video.js";
// the pure maths, still exported from here for the older import paths
export { lookFilter, gradeFilter, punchZoom, driftZoom, pushZoom, cropAt, cropStyle, pieceSpans, voiceJoinGain, voiceXfadeFrames, VOICE_XFADE_FRAMES } from "../math/video.js";

export const Grain: React.FC<{ amount: number }> = ({ amount }) => (amount > 0 ? <div style={{ position: "absolute", inset: 0, pointerEvents: "none", backgroundImage: `url("${GRAIN_URI}")`, backgroundSize: `${GRAIN_TILE}px ${GRAIN_TILE}px`, opacity: grainOpacity(amount), mixBlendMode: "overlay" }} /> : null);
export const Bars: React.FC<{ on: boolean }> = ({ on }) => (on ? <>
  <div style={{ position: "absolute", left: 0, right: 0, top: 0, height: HEIGHT * BARS_FRACTION, background: "#000", pointerEvents: "none" }} />
  <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: HEIGHT * BARS_FRACTION, background: "#000", pointerEvents: "none" }} />
</> : null);
/** A warm light leak drifting across the frame (screen blend). `amount` 0..1 fades it. */
export const LightLeak: React.FC<{ on: boolean; amount?: number }> = ({ on, amount = 1 }) => {
  const frame = useCurrentFrame();
  if (!on || amount <= 0) return null;
  const p = leakPosition(frame);
  return <div style={{ position: "absolute", inset: 0, pointerEvents: "none", mixBlendMode: "screen", opacity: p.opacity * amount, background: `radial-gradient(ellipse 45% 60% at ${p.x * 100}% ${p.y * 100}%, rgba(255,170,80,0.95) 0%, rgba(255,90,60,0.55) 35%, rgba(255,60,120,0.25) 60%, rgba(0,0,0,0) 78%)` }} />;
};
/** Slow golden sparkles floating up. */
export const Sparkles: React.FC<{ on: boolean; color?: string; amount?: number }> = ({ on, color = "#F5C542", amount = 1 }) => {
  const frame = useCurrentFrame();
  if (!on || amount <= 0) return null;
  return (
    <svg style={{ position: "absolute", inset: 0, pointerEvents: "none", opacity: amount }} width={WIDTH} height={HEIGHT} viewBox={`0 0 ${WIDTH} ${HEIGHT}`}>
      {sparkles(frame).map((p, i) => (
        <g key={i} transform={`translate(${p.x * WIDTH} ${p.y * HEIGHT})`} opacity={p.a}>
          <circle r={p.r} fill={color} style={{ filter: `blur(${p.r * 0.25}px)` }} />
          <path d={`M0 ${-p.r * 2.6} L${p.r * 0.5} 0 L0 ${p.r * 2.6} L${-p.r * 0.5} 0 Z`} fill={color} opacity={0.8} />
          <path d={`M${-p.r * 2.6} 0 L0 ${p.r * 0.5} L${p.r * 2.6} 0 L0 ${-p.r * 0.5} Z`} fill={color} opacity={0.8} />
        </g>
      ))}
    </svg>
  );
};
/** A thin golden ring framing the speaker around the face-follow centre. */
export const SpeakerRing: React.FC<{ clip: Clip; on: boolean; color?: string; amount?: number }> = ({ clip, on, color = "#F5C542", amount = 1 }) => {
  const frame = useCurrentFrame();
  if (!on || amount <= 0) return null;
  const pulse = 1 + 0.015 * Math.sin(frame * 0.12);
  const size = WIDTH * (0.74 + 0.08 * vary(clipSeed(clip), 7)) * pulse;
  return (
    <div style={{ position: "absolute", left: WIDTH / 2 - size / 2, top: HEIGHT * 0.42 - size / 2, width: size, height: size, borderRadius: "50%", border: `6px solid ${color}`, boxShadow: `0 0 40px ${color}66, inset 0 0 40px ${color}33`, pointerEvents: "none", opacity: 0.9 * amount }} />
  );
};

/** The directed layers: sparkles, ring and leak appear only in their windows (or all through when the clip-wide switch is on). */
export const DirectedLayers: React.FC<{ clip: Clip; accent: string; lite?: boolean }> = ({ clip, accent, lite }) => {
  const frame = useCurrentFrame();
  const sourceMs = timelineToSource(clip.segments, frameToMs(frame));
  const amt = (on: boolean, kind: "sparkle" | "ring" | "leak") => (on ? 1 : windowStrength(clip, kind, sourceMs));
  const leak = amt(clip.effects.lightLeak, "leak"), ring = amt(clip.effects.ring, "ring"), sparkle = amt(clip.effects.sparkle, "sparkle");
  if (lite) return null;
  return (
    <>
      <LightLeak on={leak > 0} amount={leak} />
      <SpeakerRing clip={clip} on={ring > 0} amount={ring} color={accent} />
      <Sparkles on={sparkle > 0} amount={sparkle} color={accent} />
    </>
  );
};
const SegmentVideo: React.FC<{ clip: Clip; segIndex: number; mediaUrl: string; mediaStartMs: number; layout: Clip["layout"]; muted?: boolean; behind?: React.ReactNode; cutoutUrl?: string; lite?: boolean }> = ({ clip, segIndex, mediaUrl, mediaStartMs, layout, muted, behind, cutoutUrl, lite }) => {
  const frame = useCurrentFrame();
  void muted; // the picture is always silent: the voice is the VoiceTrack's, crossfaded piece to piece
  const seg = clip.segments[segIndex];
  const startFrom = Math.max(0, msToFrame(seg.inMs - mediaStartMs));
  if (layout === "split") {
    const fgH = (WIDTH * SRC_H) / SRC_W;
    const top = HEIGHT * 0.4 - fgH / 2;
    return (
      <AbsoluteFill style={{ filter: lookFilter(clip.look.preset, clip.look.strength) }}>
        <div style={{ position: "absolute", width: FIT_W * 1.1, height: HEIGHT * 1.1, left: (WIDTH - FIT_W * 1.1) / 2, top: -HEIGHT * 0.05, filter: "blur(40px) brightness(0.45)" }}>
          <PVideo src={mediaUrl} startFrom={startFrom} muted pauseWhenBuffering acceptableTimeShiftInSeconds={0.6} style={{ width: "100%", height: "100%" }} />
        </div>
        <div style={{ position: "absolute", width: WIDTH, height: fgH, left: 0, top, boxShadow: "0 30px 80px rgba(0,0,0,0.6)" }}>
          <PVideo src={mediaUrl} startFrom={startFrom} muted pauseWhenBuffering acceptableTimeShiftInSeconds={0.6} style={{ width: "100%", height: "100%" }} />
        </div>
        {behind}
        {cutoutUrl ? <div style={{ position: "absolute", width: WIDTH, height: fgH, left: 0, top }}><PVideo src={cutoutUrl} startFrom={startFrom} transparent muted pauseWhenBuffering acceptableTimeShiftInSeconds={0.6} style={{ width: "100%", height: "100%" }} /></div> : null}
      </AbsoluteFill>
    );
  }
  // the crop with every zoom folded in, and the cut punctuation: the same numbers every renderer draws from
  const { crop: k, shake, flicker, glitch, flash } = pieceFrame(clip, segIndex, frame);
  // light mode (a phone's preview): no colour grade, no flicker, no glitch layers — the crop, cuts and words are what matter there
  const filter = lite ? undefined : gradeFilter(clip.look.preset, clip.look.strength, flicker);
  const video = <PVideo src={mediaUrl} startFrom={startFrom} muted pauseWhenBuffering acceptableTimeShiftInSeconds={lite ? 1.2 : 0.6} style={{ width: "100%", height: "100%" }} />;
  // the room behind the speaker: blurred, darkened or a flat colour, with the speaker's cutout drawn sharp over it
  const bg = cutoutUrl && !lite ? clip.background : null;
  const roomFilter = bg?.mode === "blur" ? `${filter && filter !== "none" ? `${filter} ` : ""}blur(${Math.round(8 + 28 * bg.amount)}px) brightness(${(1 - 0.35 * bg.amount).toFixed(2)})` : bg?.mode === "dark" ? `${filter && filter !== "none" ? `${filter} ` : ""}brightness(${(1 - 0.85 * bg.amount).toFixed(2)}) saturate(0.7)` : filter;
  return (
    <AbsoluteFill data-edit="video" style={{ overflow: "hidden", background: bg?.mode === "colour" ? bg.colour : undefined }}>
      <div style={{ position: "absolute", inset: 0, transform: shake.x || shake.y ? `translate(${shake.x}px, ${shake.y}px) rotate(${shake.rot}deg) scale(1.04)` : undefined }}>
        {bg?.mode === "colour" ? null : <div style={{ ...cropStyle(k), filter: roomFilter }}>{video}</div>}
        {glitch > 0 && !lite ? (
          <>
            <div style={{ ...cropStyle(k), filter, opacity: 0.6 * glitch, mixBlendMode: "screen", transform: `translateX(${-14 * glitch}px)`, clipPath: `inset(${20 + glitch * 10}% 0 ${30 - glitch * 10}% 0)` }}>{video}</div>
            <div style={{ ...cropStyle(k), filter: `${filter} hue-rotate(120deg)`, opacity: 0.6 * glitch, mixBlendMode: "screen", transform: `translateX(${14 * glitch}px)`, clipPath: `inset(${55 - glitch * 8}% 0 ${10 + glitch * 8}% 0)` }}>{video}</div>
          </>
        ) : null}
      </div>
      {behind || cutoutUrl ? (
        // words behind the speaker: drawn over the room, then the speaker's cutout (same crop, same grade) is drawn over the words
        <div style={{ position: "absolute", inset: 0, transform: shake.x || shake.y ? `translate(${shake.x}px, ${shake.y}px) rotate(${shake.rot}deg) scale(1.04)` : undefined }}>
          {behind}
          {cutoutUrl ? <div style={{ ...cropStyle(k), filter }}><PVideo src={cutoutUrl} startFrom={startFrom} transparent muted pauseWhenBuffering acceptableTimeShiftInSeconds={0.6} style={{ width: "100%", height: "100%" }} /></div> : null}
        </div>
      ) : null}
      {clip.look.vignette > 0 ? <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: `radial-gradient(ellipse at 50% 45%, rgba(0,0,0,0) 55%, rgba(0,0,0,${vignetteAlpha(clip.look.vignette)}) 100%)` }} /> : null}
      {flash > 0 ? <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "#fff", opacity: flash }} /> : null}
    </AbsoluteFill>
  );
};

/**
 * One piece inside a transition: it animates in over the cut before it (`overlapIn` frames) while the previous piece keeps playing
 * behind, and plays on through the cut after it (`overlap` frames) while the next arrives. Each cut has its own style.
 */
const TransitionPiece: React.FC<{ clip: Clip; segIndex: number; overlap: number; localFrame: number; pieceFrames: number; children: React.ReactNode }> = ({ clip, segIndex, overlap, localFrame, pieceFrames, children }) => {
  const overlapIn = segIndex === 0 ? 0 : transitionFrames(clip, segIndex - 1);
  const enter = overlapIn > 0 ? localFrame / overlapIn : 1;
  const isLast = segIndex === clip.segments.length - 1;
  const leave = isLast || overlap <= 0 ? 0 : (localFrame - (pieceFrames - overlap)) / overlap;
  const inMotion = incomingMotion(styleInto(clip, segIndex), enter, segIndex, useLite());
  const outStyle = outgoingMotion(cutAt(clip, segIndex).style, leave, segIndex + 1, useLite());
  return (
    <AbsoluteFill style={{ ...outStyle, ...inMotion.style }}>
      {children}
      {inMotion.overlayOpacity > 0 ? <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: inMotion.overlayColor, opacity: inMotion.overlayOpacity }} /> : null}
    </AbsoluteFill>
  );
};

const TransitionFrame: React.FC<{ clip: Clip; segIndex: number; overlap: number; pieceFrames: number; children: React.ReactNode }> = ({ clip, segIndex, overlap, pieceFrames, children }) => {
  const localFrame = useCurrentFrame();
  return <TransitionPiece clip={clip} segIndex={segIndex} overlap={overlap} localFrame={localFrame} pieceFrames={pieceFrames}>{children}</TransitionPiece>;
};

/** A whole layer arriving with a transition over its first `frames` frames (the video after the opener, the end card after the video). */
export const ArriveWith: React.FC<{ style: CutStyle; frames: number; index?: number; children: React.ReactNode }> = ({ style, frames, index = 1, children }) => {
  const frame = useCurrentFrame();
  const m = frames > 0 ? incomingMotion(style, frame / frames, index, useLite()) : { style: {}, overlayOpacity: 0, overlayColor: "#fff" };
  return (
    <AbsoluteFill style={m.style}>
      {children}
      {m.overlayOpacity > 0 ? <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: m.overlayColor, opacity: m.overlayOpacity }} /> : null}
    </AbsoluteFill>
  );
};
/** A whole layer leaving with a transition: `t` runs 0..1 from `from` over `frames` frames of the layer's own clock. */
export const LeaveWith: React.FC<{ style: CutStyle; from: number; frames: number; index?: number; children: React.ReactNode }> = ({ style, from, frames, index = 1, children }) => {
  const frame = useCurrentFrame();
  const t = frames > 0 ? (frame - from) / frames : 0;
  return <AbsoluteFill style={outgoingMotion(style, t, index, useLite())}>{children}</AbsoluteFill>;
};

/** The voice: one audio sequence per piece, each running on through the join after it while the next fades in, at the clip's level. */
export const VoiceTrack: React.FC<{ clip: Clip; mediaUrl: string; mediaStartMs: number }> = ({ clip, mediaUrl, mediaStartMs }) => {
  const level = voiceGain(clip);
  if (level === 0) return null;
  const fading = clip.voiceFade.inMs > 0 || clip.voiceFade.outMs > 0;
  let from = 0;
  return (
    <>
      {clip.segments.map((seg, i) => {
        const own = Math.max(1, msToFrame(seg.outMs - seg.inMs));
        const tail = voiceXfadeFrames(clip, i);
        const pieceStartMs = frameToMs(from);
        const volume = (f: number) => level * voiceJoinGain(clip, i, f) * (fading ? voiceFadeGain(pieceStartMs + frameToMs(f), clip.segments, clip.voiceFade) : 1);
        const el = (
          <Sequence key={i} from={from} durationInFrames={own + tail} name={`voice ${i + 1}`} layout="none">
            <PAudio src={mediaUrl} startFrom={Math.max(0, msToFrame(seg.inMs - mediaStartMs))} volume={volume} />
          </Sequence>
        );
        from += own;
        return el;
      })}
    </>
  );
};

/** Plays the kept segments back to back (jump cuts / transitions) with face-follow crop / punch zoom. */
export const SourceVideo: React.FC<{ clip: Clip; mediaUrl: string; mediaStartMs: number; muted?: boolean; /** transparent video of the speaker alone (same cut as mediaUrl): words go behind them */ cutoutUrl?: string; /** preview on a phone: skip the grade and the heavy layers */ lite?: boolean }> = ({ clip, mediaUrl, mediaStartMs, muted, cutoutUrl, lite }) => {
  const spans = pieceSpans(clip);
  const transition = anyTransition(clip);
  // tolerant of a clip saved before this field existed (an older server can feed a newer bundle for a moment)
  const hasBehind = (clip.behindTexts ?? []).some((t) => t.enabled && t.text.trim());
  return (
    <>
      {muted ? null : <VoiceTrack clip={clip} mediaUrl={mediaUrl} mediaStartMs={mediaStartMs} />}
      {clip.segments.map((seg, i) => {
        const { from, dur, overlap } = spans[i];
        const behind = hasBehind ? <BehindTexts clip={clip} pieceFrom={from} pieceFrames={dur} /> : undefined;
        const piece = <SegmentVideo clip={clip} segIndex={i} mediaUrl={mediaUrl} mediaStartMs={mediaStartMs} layout={clip.layout} muted={muted} behind={behind} cutoutUrl={hasBehind ? cutoutUrl : undefined} lite={lite} />;
        return (
          <Sequence key={i} from={from} durationInFrames={dur} name={`seg ${i + 1}`} premountFor={i === 0 ? 0 : lite ? 45 : 20}>
            {transition ? <TransitionFrame clip={clip} segIndex={i} overlap={overlap} pieceFrames={dur}>{piece}</TransitionFrame> : piece}
          </Sequence>
        );
      })}
    </>
  );
};

export const FPS_EXPORT = FPS;
export const interpolateSafe = interpolate;
